/*
  Reliable Textiles - storefront additions. Loaded on every page from layout/theme.liquid.

  1. Order table on product pages (snippets/quick-order-list*.liquid):
     pack buttons, quantity, add to cart, cart bar on phones.
  2. Cart panel (snippets/cart-drawer.liquid): opens after an add with an "Added" note.
  3. Shipping line: a live quote for the cart, shown in the cart panel, under the order
     table and in the phone cart bar.
  4. Discount codes in the cart panel: a code box above the totals; codes are added to the
     cart with cart/update.js, so the panel shows the discounted total before checkout.
  5. Offer bar pinned to the top: keeps --rt-bar-height (rt-store.css, section 6) equal to
     the announcement bar's height, so the sticky header sits right under the bar.
*/
(function () {
  'use strict';
  if (window.RTStore) return;

  /* Orders at or above this amount ship free. Keep it in step with the automatic discount
     "Free shipping on orders of $3,500 or more" (Shopify admin > Discounts). */
  var FREE_SHIPPING_FROM_CENTS = 350000;

  /* Shipping is a flat rate by weight across the contiguous US, so one fixed address
     returns the amount every buyer there is charged at checkout. */
  var QUOTE_ADDRESS = { zip: '30043', province: 'Georgia', country: 'United States' };

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var WHERE = 'to the 48 contiguous states';

  /* ---------------------------------------------------------------- helpers */

  function money(cents) {
    var n = Math.round(Number(cents) || 0);
    var s = (Math.abs(n) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (n < 0 ? '-$' : '$') + s;
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  // Only touch the DOM when the text really changes (keeps mutation observers quiet).
  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  /* ------------------------------------------------------------------- cart */

  var cartRequest = null;
  var cartRequestAt = 0;

  function getCart(fresh) {
    var now = Date.now();
    if (!fresh && cartRequest && now - cartRequestAt < 1500) return cartRequest;
    cartRequestAt = now;
    cartRequest = fetch(ROOT + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(
      function (res) {
        if (!res.ok) throw new Error('cart ' + res.status);
        return res.json();
      }
    );
    cartRequest.catch(function () {
      cartRequest = null;
    });
    return cartRequest;
  }

  // Same format as data-sig in snippets/cart-drawer.liquid.
  function signature(cart) {
    return (cart.items || [])
      .map(function (item) {
        return item.variant_id + 'x' + item.quantity;
      })
      .join(',');
  }

  /* --------------------------------------------------------- shipping quote */

  var CACHE_KEY = 'rt-ship-quote';
  var quoteQueue = Promise.resolve();
  var quoteBySignature = {};

  function readCache(sig) {
    try {
      var saved = JSON.parse(window.sessionStorage.getItem(CACHE_KEY) || 'null');
      if (saved && saved.sig === sig && Date.now() - saved.at < 30 * 60 * 1000) return saved.quote;
    } catch (e) {}
    return null;
  }

  function writeCache(sig, quote) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ sig: sig, quote: quote, at: Date.now() }));
    } catch (e) {}
  }

  async function fetchRates() {
    var query =
      'shipping_address%5Bzip%5D=' +
      encodeURIComponent(QUOTE_ADDRESS.zip) +
      '&shipping_address%5Bcountry%5D=' +
      encodeURIComponent(QUOTE_ADDRESS.country) +
      '&shipping_address%5Bprovince%5D=' +
      encodeURIComponent(QUOTE_ADDRESS.province);
    var options = { credentials: 'same-origin', headers: { Accept: 'application/json' } };

    var prepared = await fetch(ROOT + 'cart/prepare_shipping_rates.json?' + query, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!prepared.ok) throw new Error('prepare ' + prepared.status);

    for (var attempt = 0; attempt < 14; attempt++) {
      await sleep(attempt < 4 ? 350 : 700);
      var res = await fetch(ROOT + 'cart/async_shipping_rates.json?' + query, options);
      if (res.status === 200) {
        var data = await res.json().catch(function () {
          return null;
        });
        if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
      } else if (res.status !== 202) {
        throw new Error('rates ' + res.status);
      }
    }
    throw new Error('rates timed out');
  }

  /* Resolves to { state: 'free' | 'paid' | 'unknown', cents }.
     sig and totalCents describe the cart the quote is for. */
  function quote(sig, totalCents) {
    if (!sig) return Promise.resolve({ state: 'unknown', cents: 0 });
    if (totalCents >= FREE_SHIPPING_FROM_CENTS) return Promise.resolve({ state: 'free', cents: 0 });

    var cached = readCache(sig);
    if (cached) return Promise.resolve(cached);

    if (!quoteBySignature[sig]) {
      var job = quoteQueue
        .then(function () {
          return getCart(true);
        })
        .then(function (cart) {
          // The cart moved on while this quote waited its turn: leave it to the newer request.
          if (signature(cart) !== sig) return { state: 'unknown', cents: 0, stale: true };
          return fetchRates().then(function (rates) {
            var lowest = null;
            rates.forEach(function (rate) {
              var cents = Math.round(parseFloat(rate.price) * 100);
              if (!isNaN(cents) && (lowest === null || cents < lowest)) lowest = cents;
            });
            if (lowest === null) return { state: 'unknown', cents: 0 };
            var result = { state: lowest === 0 ? 'free' : 'paid', cents: lowest };
            writeCache(sig, result);
            return result;
          });
        })
        .catch(function () {
          return { state: 'unknown', cents: 0, failed: true };
        })
        .then(function (result) {
          if (result.stale || result.failed) delete quoteBySignature[sig];
          return result;
        });
      quoteBySignature[sig] = job;
      quoteQueue = job;
    }
    return quoteBySignature[sig];
  }

  /* ------------------------------------------------------------- cart panel */

  function paintDrawerTotals() {
    var box = document.querySelector('cart-drawer [data-rt-totals]');
    if (!box) return;
    // The panel is re-rendered on every cart change, so each box is painted once.
    if (box.getAttribute('data-painted')) return;
    box.setAttribute('data-painted', '1');
    if (knownCodes) paintCodes(knownCodes);
    var sig = box.getAttribute('data-sig') || '';
    if (!sig) return;

    var total = parseInt(box.getAttribute('data-total-cents'), 10) || 0;
    var shipEl = box.querySelector('[data-rt-ship]');
    var totalEl = box.querySelector('[data-rt-total]');

    function apply(result) {
      if (!document.body.contains(box) || !shipEl || !totalEl) return;
      shipEl.classList.toggle('rt-totals__free', result.state === 'free');
      if (result.state === 'free') {
        setText(shipEl, 'Free');
        setText(totalEl, money(total));
      } else if (result.state === 'paid') {
        setText(shipEl, money(result.cents));
        setText(totalEl, money(total + result.cents));
      } else {
        setText(shipEl, 'Calculated at checkout');
        setText(totalEl, money(total));
      }
    }

    if (total < FREE_SHIPPING_FROM_CENTS && !readCache(sig)) setText(shipEl, 'Calculating…');
    var cartNow = getCart(true).catch(function () {
      return null;
    });
    Promise.all([cartNow, quote(sig, total)]).then(function (results) {
      var cart = results[0];
      var result = results[1];
      if (cart) {
        knownCodes = cart.discount_codes || [];
        paintCodes(knownCodes);
        // A free-shipping code is not part of the rate quote: Shopify applies it at checkout.
        if (signature(cart) === sig && result.state !== 'free' && shippingCodes(cart).length) {
          result = { state: 'free', cents: 0 };
        }
      }
      apply(result);
    });
  }

  function openDrawer(trigger) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return false;
    drawer.open(trigger);
    return true;
  }

  // Shows the cart panel with the content Shopify returned for the add request.
  function showAdded(state, trigger) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') return false;
    if (!state || !state.sections || !state.sections['cart-drawer'] || !state.sections['cart-icon-bubble']) return false;
    try {
      if (trigger && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(trigger);
      drawer.classList.remove('is-empty');
      drawer.classList.add('rt-just-added');
      drawer.renderContents(state);
      return true;
    } catch (e) {
      return false;
    }
  }

  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var wasActive = drawer.classList.contains('active');
    var repaint = debounce(function () {
      if (drawer.classList.contains('active')) paintDrawerTotals();
    }, 40);

    new MutationObserver(function () {
      var active = drawer.classList.contains('active');
      if (wasActive && !active) drawer.classList.remove('rt-just-added');
      wasActive = active;
      repaint();
    }).observe(drawer, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });

    // The "Added" note is about the last add only: drop it once the buyer edits the cart.
    drawer.addEventListener('change', function () {
      drawer.classList.remove('rt-just-added');
    });
    drawer.addEventListener('click', function (event) {
      if (event.target.closest('cart-remove-button')) drawer.classList.remove('rt-just-added');
    });
  }

  /* ------------------------------------------------ cart panel: discount codes */

  // Liquid cannot list the codes on a cart, so the code tags are drawn from cart.js.
  // The last known list is kept so a re-rendered panel shows it straight away.
  var knownCodes = null;
  var codeBusy = false;

  function normCode(code) {
    return String(code || '').trim().toUpperCase();
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Codes the cart accepts that take nothing off the items are shipping codes (FREESHIPPING):
     Shopify applies them to the shipping at checkout. */
  function shippingCodes(cart) {
    var onItems = {};
    (cart.cart_level_discount_applications || []).forEach(function (app) {
      onItems[normCode(app.title)] = true;
    });
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        if (allocation.discount_application) onItems[normCode(allocation.discount_application.title)] = true;
      });
    });
    return (cart.discount_codes || [])
      .filter(function (c) {
        return c.applicable && !onItems[normCode(c.code)];
      })
      .map(function (c) {
        return normCode(c.code);
      });
  }

  function paintCodes(codes) {
    var list = document.querySelector('cart-drawer [data-rt-code-list]');
    if (!list || !codes) return;
    var key = codes
      .map(function (c) {
        return normCode(c.code) + (c.applicable ? '+' : '-');
      })
      .join(',');
    if (list.getAttribute('data-key') === key) return;
    list.setAttribute('data-key', key);
    list.innerHTML = codes
      .map(function (c) {
        var code = escapeHtml(normCode(c.code));
        return (
          '<li class="rt-codes__chip' +
          (c.applicable ? '' : ' rt-codes__chip--off') +
          '"><span class="rt-codes__code">' +
          code +
          '</span>' +
          (c.applicable ? '' : '<span class="rt-codes__why">doesn’t apply right now</span>') +
          '<button type="button" class="rt-codes__remove" data-rt-code-remove="' +
          code +
          '" aria-label="Remove code ' +
          code +
          '"><span aria-hidden="true">×</span></button></li>'
        );
      })
      .join('');
    list.hidden = !codes.length;
  }

  function setCodeMessage(text, isError) {
    var msg = document.querySelector('cart-drawer [data-rt-code-msg]');
    if (!msg) return;
    msg.classList.toggle('rt-codes__msg--error', !!isError);
    setText(msg, text || '');
  }

  // Puts the panel Shopify returned in place, keeping the item list where the buyer left it.
  function renderDrawerFrom(state) {
    var html = state && state.sections && state.sections['cart-drawer'];
    var inner = document.querySelector('#CartDrawer .drawer__inner');
    if (!html || !inner) return false;
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.drawer__inner');
    if (!fresh) return false;
    var items = inner.querySelector('cart-drawer-items');
    var scrollTop = items ? items.scrollTop : 0;
    inner.innerHTML = fresh.innerHTML;
    var newItems = inner.querySelector('cart-drawer-items');
    if (newItems) newItems.scrollTop = scrollTop;
    return true;
  }

  // Sets the cart's codes to exactly `codes`. Resolves to the new cart, with the panel's HTML.
  async function postCodes(codes) {
    var res = await fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(','), sections: ['cart-drawer'], sections_url: window.location.pathname }),
    });
    var state = await res.json().catch(function () {
      return {};
    });
    if (!res.ok) throw new Error(state.description || state.message || 'The code could not be checked. Please try again.');
    knownCodes = state.discount_codes || [];
    cartRequest = null;
    return state;
  }

  function showCart(state) {
    var drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.remove('rt-just-added');
    renderDrawerFrom(state);
    paintDrawerTotals();
    refreshCartViews();
  }

  function focusInDrawer(selector) {
    var el = document.querySelector('cart-drawer ' + selector);
    var container = document.getElementById('CartDrawer');
    if (!el) return;
    if (container && typeof window.trapFocus === 'function') window.trapFocus(container, el);
    else el.focus();
  }

  function setCodeBusy(on, label) {
    var button = document.querySelector('cart-drawer .rt-codes__apply');
    if (!button) return;
    button.disabled = on;
    if (label) button.textContent = label;
  }

  async function onApplyCode(form) {
    if (codeBusy) return;
    var input = form.querySelector('input[name="discount"]');
    var entered = String((input && input.value) || '')
      .split(/[\s,]+/)
      .map(normCode)
      .filter(Boolean);
    if (!entered.length) {
      setCodeMessage('Enter a discount code.', true);
      if (input) input.focus();
      return;
    }
    codeBusy = true;
    setCodeBusy(true, 'Applying…');
    setCodeMessage('');
    try {
      var cart = await getCart(true);
      var codes = (cart.discount_codes || []).map(function (c) {
        return normCode(c.code);
      });
      entered.forEach(function (code) {
        if (codes.indexOf(code) === -1) codes.push(code);
      });
      var state = await postCodes(codes);

      var status = {};
      (state.discount_codes || []).forEach(function (c) {
        status[normCode(c.code)] = c.applicable;
      });
      var refused = entered.filter(function (code) {
        return !status[code];
      });
      if (refused.length) {
        // A code that does nothing for this cart is taken off again, so it never reaches checkout.
        state = await postCodes(
          codes.filter(function (code) {
            return refused.indexOf(code) === -1;
          })
        );
      }
      showCart(state);
      if (refused.length) {
        setCodeMessage(refused.join(', ') + (refused.length > 1 ? ' don’t' : ' doesn’t') + ' apply to the items in your cart.', true);
        var again = document.querySelector('cart-drawer input[name="discount"]');
        if (again) again.value = refused.join(', ');
        focusInDrawer('input[name="discount"]');
      } else {
        setCodeMessage(entered.join(', ') + ' applied.');
        focusInDrawer('.rt-codes__apply');
      }
    } catch (error) {
      setCodeBusy(false, 'Apply');
      setCodeMessage((error && error.message) || 'The code could not be checked. Please try again.', true);
    } finally {
      codeBusy = false;
    }
  }

  async function onRemoveCode(button) {
    if (codeBusy) return;
    var code = normCode(button.getAttribute('data-rt-code-remove'));
    codeBusy = true;
    button.disabled = true;
    try {
      var cart = await getCart(true);
      var state = await postCodes(
        (cart.discount_codes || [])
          .map(function (c) {
            return normCode(c.code);
          })
          .filter(function (c) {
            return c !== code;
          })
      );
      showCart(state);
      setCodeMessage(code + ' removed.');
      focusInDrawer('input[name="discount"]');
    } catch (error) {
      button.disabled = false;
      setCodeMessage((error && error.message) || 'The code could not be removed. Please try again.', true);
    } finally {
      codeBusy = false;
    }
  }

  /* ------------------------------------------- order table: cart bar + note */

  var viewsRun = 0;

  function ensureBar() {
    var bar = document.getElementById('rt-cartbar');
    if (bar) return bar;
    bar = document.createElement('div');
    bar.id = 'rt-cartbar';
    bar.className = 'rt-cartbar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Your cart');
    bar.hidden = true;
    bar.innerHTML =
      '<div class="rt-cartbar__text"><strong class="rt-cartbar__main"></strong>' +
      '<span class="rt-cartbar__sub"></span></div>' +
      '<button type="button" class="rt-cartbar__button" data-rt-view-cart>View cart</button>';
    document.body.appendChild(bar);
    return bar;
  }

  function paintCartViews(cart, result) {
    var count = cart.item_count || 0;
    var bar = ensureBar();
    var shipNote;
    var barNote = 'In your cart';

    if (!count) {
      shipNote = null; // default text from the template
    } else if (!result) {
      shipNote = 'Shipping for your cart: calculating…';
    } else if (result.state === 'free') {
      shipNote = 'Your cart ships free ' + WHERE + '.';
      barNote = 'In your cart · ships free';
    } else if (result.state === 'paid') {
      shipNote = 'Shipping for your cart: ' + money(result.cents) + ' ' + WHERE + '.';
      barNote = 'In your cart · + ' + money(result.cents) + ' shipping';
    } else {
      shipNote = 'Shipping is calculated at checkout.';
    }

    document.querySelectorAll('[data-rt-shipline]').forEach(function (el) {
      setText(el, shipNote === null ? el.getAttribute('data-default') || '' : shipNote);
      el.setAttribute('data-rt-painted', '1');
    });

    if (!count) {
      bar.hidden = true;
      document.documentElement.classList.remove('rt-has-cartbar');
      return;
    }
    setText(bar.querySelector('.rt-cartbar__main'), count + (count === 1 ? ' item' : ' items') + ' · ' + money(cart.total_price));
    setText(bar.querySelector('.rt-cartbar__sub'), barNote);
    bar.hidden = false;
    document.documentElement.classList.add('rt-has-cartbar');
  }

  function refreshCartViews() {
    if (!document.querySelector('quick-order-list')) return;
    var run = ++viewsRun;
    getCart(true)
      .then(function (cart) {
        if (run !== viewsRun) return;
        var sig = signature(cart);
        var known = cart.total_price >= FREE_SHIPPING_FROM_CENTS ? { state: 'free', cents: 0 } : readCache(sig);
        if (shippingCodes(cart).length) known = { state: 'free', cents: 0 };
        paintCartViews(cart, cart.item_count ? known : null);
        if (!cart.item_count || known) return;
        return quote(sig, cart.total_price).then(function (result) {
          if (run === viewsRun) paintCartViews(cart, result);
        });
      })
      .catch(function () {});
  }

  /* --------------------------------------------------- order table: rows */

  function getSelected(row) {
    var btn = row.querySelector('.pack-button--selected');
    if (!btn) return null;
    return {
      button: btn,
      variantId: btn.dataset.variantId,
      priceCents: parseInt(btn.dataset.priceCents, 10) || 0,
      compareCents: parseInt(btn.dataset.comparePriceCents, 10) || 0,
      eachCents: parseInt(btn.dataset.eachCents, 10) || 0,
      unit: btn.dataset.unit || 'per piece',
      isSample: btn.dataset.isSample === 'true',
      inventoryTracked: btn.dataset.inventoryTracked === 'true',
      inventoryPolicy: btn.dataset.inventoryPolicy || 'deny',
      inventoryQuantity: parseInt(btn.dataset.inventoryQuantity, 10) || 0,
    };
  }

  function maxQtyFor(sel) {
    if (sel.isSample) return 1;
    if (sel.inventoryTracked && sel.inventoryPolicy === 'deny') return Math.max(1, sel.inventoryQuantity);
    return 999999;
  }

  function updateTotal(row) {
    var sel = getSelected(row);
    if (!sel) return;
    var qtyInput = row.querySelector('.quantity__input');
    var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;

    setText(row.querySelector('.totalPrice'), money(sel.priceCents * qty));

    var compare = row.querySelector('.comparePrice');
    if (compare) {
      if (sel.compareCents > sel.priceCents) {
        setText(compare, money(sel.compareCents * qty));
        compare.style.display = 'block';
      } else {
        compare.style.display = 'none';
      }
    }

    setText(row.querySelector('.rt-row__each-price'), money(sel.eachCents));
    setText(row.querySelector('.rt-row__each-unit'), sel.unit);
  }

  function applyQtyState(row) {
    var sel = getSelected(row);
    var qtyInput = row.querySelector('.quantity__input');
    var wrapper = row.querySelector('.variant-item__quantity-wrapper');
    if (!sel || !qtyInput || !wrapper) return;
    var incBtn = row.querySelector('[data-action="increment"]');
    var decBtn = row.querySelector('[data-action="decrement"]');
    var max = maxQtyFor(sel);

    if (sel.isSample) {
      qtyInput.value = 1;
      qtyInput.readOnly = true;
      wrapper.classList.add('disabled-popover');
      wrapper.classList.remove('enabled-popover');
    } else {
      qtyInput.readOnly = false;
      qtyInput.disabled = false;
      wrapper.classList.remove('disabled-popover');
      wrapper.classList.add('enabled-popover');
    }
    qtyInput.setAttribute('max', max);
    var q = parseInt(qtyInput.value, 10) || 1;
    if (q < 1) q = 1;
    if (q > max) q = max;
    qtyInput.value = q;
    if (incBtn) incBtn.disabled = q >= max || sel.isSample;
    if (decBtn) decBtn.disabled = q <= 1 || sel.isSample;
  }

  function sampleOfProductInCart(cart, productId) {
    var pid = parseInt(productId, 10);
    if (!pid || !cart || !cart.items) return false;
    return cart.items.some(function (item) {
      return (
        item.product_id === pid &&
        item.properties &&
        (item.properties._is_sample === 'true' || item.properties._is_sample === true)
      );
    });
  }

  function setAddButtons(row, disabled, title) {
    row.querySelectorAll('.add-to-cart-button').forEach(function (b) {
      if (b.classList.contains('loading')) return;
      b.disabled = disabled;
      b.title = title || '';
    });
  }

  // Says why the button is off (a tooltip alone never shows on a phone).
  function setLimitNote(row, on) {
    var box = row.querySelector('.rt-row__msg');
    if (!box) return;
    if (on) {
      clearTimeout(box._rtTimer);
      box.textContent = 'One sample per product. A sample of this product is already in your cart.';
      box.classList.add('rt-row__msg--note');
      box.hidden = false;
    } else if (box.classList.contains('rt-row__msg--note')) {
      box.classList.remove('rt-row__msg--note');
      box.textContent = '';
      box.hidden = true;
    }
  }

  // Limit: one sample per product in the cart.
  function refreshAddButton(row) {
    var sel = getSelected(row);
    if (!sel) {
      setAddButtons(row, true, 'Sold out');
      setLimitNote(row, false);
      return;
    }
    if (!sel.isSample) {
      setAddButtons(row, false, '');
      setLimitNote(row, false);
      return;
    }
    getCart(false)
      .then(function (cart) {
        var current = getSelected(row);
        if (!current || !current.isSample) return;
        var blocked = sampleOfProductInCart(cart, row.dataset.productId);
        setAddButtons(row, blocked, blocked ? 'Limit: one sample per product' : '');
        setLimitNote(row, blocked);
      })
      .catch(function () {});
  }

  function markPressed(row) {
    row.querySelectorAll('.pack-button').forEach(function (b) {
      b.setAttribute('aria-pressed', b.classList.contains('pack-button--selected') ? 'true' : 'false');
    });
  }

  function selectButton(row, button) {
    if (button.disabled) return;
    row.querySelectorAll('.pack-button').forEach(function (b) {
      b.classList.remove('pack-button--selected');
    });
    button.classList.add('pack-button--selected');
    row.dataset.selectedVariantId = button.dataset.variantId;
    markPressed(row);
    applyQtyState(row);
    updateTotal(row);
    refreshAddButton(row);
  }

  function showRowMessage(row, text) {
    var box = row.querySelector('.rt-row__msg');
    if (!box) return;
    box.classList.remove('rt-row__msg--note');
    box.textContent = text;
    box.hidden = false;
    clearTimeout(box._rtTimer);
    box._rtTimer = setTimeout(function () {
      box.hidden = true;
    }, 6000);
  }

  function onQtyButton(button) {
    var row = button.closest('tr.variant-item');
    var sel = row && getSelected(row);
    if (!sel || sel.isSample) return;
    var qtyInput = row.querySelector('.quantity__input');
    var max = maxQtyFor(sel);
    var q = parseInt(qtyInput.value, 10) || 1;
    if (button.dataset.action === 'increment' && q < max) q += 1;
    if (button.dataset.action === 'decrement' && q > 1) q -= 1;
    qtyInput.value = q;
    applyQtyState(row);
    updateTotal(row);
  }

  function onQtyBlur(input) {
    var row = input.closest('tr.variant-item');
    if (!row || !getSelected(row)) return;
    applyQtyState(row);
    updateTotal(row);
  }

  // Replaces the totals under the table with the copy Shopify rendered for the add request.
  function updateTotalBar(list, html) {
    if (!list || !html) return;
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.quick-order-list__total');
    var current = list.querySelector('.quick-order-list__total');
    if (fresh && current) current.innerHTML = fresh.innerHTML;
  }

  var sampleAddInFlight = false;

  async function onAddToCart(button) {
    var row = button.closest('tr.variant-item');
    var sel = row && getSelected(row);
    if (!sel || button.classList.contains('loading')) return;
    var qtyInput = row.querySelector('.quantity__input');
    var qty = sel.isSample ? 1 : parseInt(qtyInput && qtyInput.value, 10) || 1;
    var label = button.textContent;
    var list = row.closest('quick-order-list');
    var sectionId = list && list.dataset.section;

    // Only one sample add at a time, so two fast clicks cannot both get past the limit check.
    if (sel.isSample) {
      if (sampleAddInFlight) return;
      sampleAddInFlight = true;
    }

    try {
      if (sel.isSample) {
        var cart = await getCart(true).catch(function () {
          return null;
        });
        if (cart && sampleOfProductInCart(cart, row.dataset.productId)) {
          setAddButtons(row, true, 'Limit: one sample per product');
          setLimitNote(row, true);
          return;
        }
      }

      var item = { id: sel.variantId, quantity: qty };
      if (sel.isSample) {
        item.properties = { _is_sample: 'true', 'Sample of': row.dataset.productTitle || 'Product' };
      }
      var sections = ['cart-drawer', 'cart-icon-bubble'];
      if (sectionId) sections.push(sectionId);

      button.disabled = true;
      button.classList.add('loading');
      button.textContent = 'Adding…';

      var res = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: sections, sections_url: window.location.pathname }),
      });
      var state = await res.json().catch(function () {
        return {};
      });
      if (!res.ok) throw new Error(state.description || state.message || 'This could not be added to your cart.');

      cartRequest = null;
      button.textContent = 'Added';
      if (sectionId && state.sections) updateTotalBar(list, state.sections[sectionId]);
      document.dispatchEvent(new CustomEvent('cart:updated'));
      if (!showAdded(state, button)) {
        // No cart panel on this page (the theme's cart type was changed): go to the cart page.
        window.location.href = ROOT + 'cart';
        return;
      }
      refreshCartViews();

      setTimeout(function () {
        button.textContent = label;
        button.classList.remove('loading');
        button.disabled = false;
        refreshAddButton(row);
      }, 1200);
    } catch (error) {
      button.textContent = label;
      button.classList.remove('loading');
      button.disabled = false;
      showRowMessage(row, (error && error.message) || 'This could not be added to your cart.');
    } finally {
      if (sel.isSample) sampleAddInFlight = false;
    }
  }

  function initRow(row) {
    row.setAttribute('data-rt-ready', '1');

    if (!row.querySelector('.pack-button--selected:not([disabled])')) {
      row.querySelectorAll('.pack-button--selected').forEach(function (b) {
        b.classList.remove('pack-button--selected');
      });
      var firstAvailable = row.querySelector('.pack-button:not([disabled])');
      if (firstAvailable) {
        firstAvailable.classList.add('pack-button--selected');
        row.dataset.selectedVariantId = firstAvailable.dataset.variantId;
      }
    }

    markPressed(row);

    var qtyInput = row.querySelector('.quantity__input');
    if (qtyInput) {
      qtyInput.addEventListener('blur', function () {
        onQtyBlur(qtyInput);
      });
      qtyInput.addEventListener('keydown', function (event) {
        if (event.ctrlKey || event.metaKey) return;
        if (event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '.') event.preventDefault();
      });
      qtyInput.addEventListener('paste', function (event) {
        var text = (event.clipboardData || window.clipboardData).getData('text');
        if (!/^\d+$/.test(text.trim())) event.preventDefault();
      });
    }

    applyQtyState(row);
    updateTotal(row);
    refreshAddButton(row);
  }

  // Arriving from a color card (?variant=...): show that color's row first.
  function promoteLinkedRow(list) {
    var id = (new URLSearchParams(window.location.search).get('variant') || '').replace(/[^0-9]/g, '');
    if (!id) return;
    var button = list.querySelector('.pack-button[data-variant-id="' + id + '"]');
    var row = button && button.closest('tr.variant-item');
    var body = row && row.parentNode;
    if (!row || !body || body.firstElementChild === row) return;
    var errorRow = row.nextElementSibling;
    body.insertBefore(row, body.firstElementChild);
    if (errorRow && errorRow.classList.contains('desktop-row-error')) body.insertBefore(errorRow, row.nextSibling);
  }

  function initList(list) {
    var rows = list.querySelectorAll('tr.variant-item:not([data-rt-ready])');
    if (!rows.length) return false;
    rows.forEach(initRow);
    promoteLinkedRow(list);
    return true;
  }

  function initOrderTables() {
    var lists = document.querySelectorAll('quick-order-list');
    if (!lists.length) return;

    lists.forEach(function (list) {
      initList(list);
      // The theme re-renders the table when the cart changes elsewhere (cart panel, "Remove all").
      // New rows and a fresh totals block are the signs of that.
      new MutationObserver(
        debounce(function () {
          var newRows = initList(list);
          var freshTotals = list.querySelector('[data-rt-shipline]:not([data-rt-painted])');
          if (newRows || freshTotals) {
            cartRequest = null;
            refreshCartViews();
          }
        }, 80)
      ).observe(list, { childList: true, subtree: true });
    });

    refreshCartViews();
  }

  /* --------------------------------------------- offer bar pinned to the top */

  // rt-store.css pins the announcement bar to the top of the screen and moves the sticky header
  // (and the sticky product column) down by --rt-bar-height. The bar's height changes with the
  // screen width (two lines of text on phones), with font loading and when the bar is edited
  // in the theme editor, so it is measured rather than fixed.
  var barObserver = null;

  function measureOfferBar() {
    var bar = document.querySelector('.announcement-bar-section');
    var height = bar ? bar.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--rt-bar-height', height + 'px');
  }

  function watchOfferBar() {
    measureOfferBar();
    if (!('ResizeObserver' in window)) return;
    if (barObserver) barObserver.disconnect();
    barObserver = new ResizeObserver(measureOfferBar);
    var bar = document.querySelector('.announcement-bar-section');
    if (bar) barObserver.observe(bar);
  }

  if (!('ResizeObserver' in window)) window.addEventListener('resize', debounce(measureOfferBar, 100));

  // Theme editor: a section that is edited is replaced, so watch the new bar.
  document.addEventListener('shopify:section:load', watchOfferBar);
  document.addEventListener('shopify:section:unload', function () {
    setTimeout(watchOfferBar, 0);
  });

  /* ----------------------------------------------------------------- events */

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var viewCart = target.closest('[data-rt-view-cart], quick-order-list .quick-order-list__button');
      if (viewCart) {
        if (openDrawer(viewCart)) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }

      var removeCode = target.closest('[data-rt-code-remove]');
      if (removeCode) {
        event.preventDefault();
        onRemoveCode(removeCode);
        return;
      }

      if (!target.closest('quick-order-list')) return;

      var packBtn = target.closest('.pack-button[data-variant-id]');
      if (packBtn) {
        event.preventDefault();
        event.stopImmediatePropagation();
        var row = packBtn.closest('tr.variant-item');
        if (row) selectButton(row, packBtn);
        return;
      }
      var qtyBtn = target.closest('[data-action="increment"], [data-action="decrement"]');
      if (qtyBtn) {
        event.preventDefault();
        event.stopImmediatePropagation();
        onQtyButton(qtyBtn);
        return;
      }
      var addBtn = target.closest('.add-to-cart-button');
      if (addBtn) {
        event.preventDefault();
        event.stopImmediatePropagation();
        onAddToCart(addBtn);
      }
    },
    true
  );

  document.addEventListener('submit', function (event) {
    var form = event.target && event.target.closest && event.target.closest('[data-rt-code-form]');
    if (!form) return;
    event.preventDefault();
    onApplyCode(form);
  });

  function start() {
    watchOfferBar();
    watchDrawer();
    initOrderTables();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.RTStore = {
    money: money,
    quote: quote,
    refresh: refreshCartViews,
    openCart: openDrawer,
  };
})();
